import { handleVersionMismatch } from 'actions/versionSync';
import PurchaseSubscriptionErrorModal from 'components/PurchaseSubscription/PurchaseSubscriptionErrorModal';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { VersionMismatch } from 'services/BaseService';

import PaymentService from 'services/PaymentService';

import { fetch as fetchSubscription } from 'actions/subscription';

import Loading from 'components/Loading';
import getDefaultPayPalOptions from 'components/Payment/getDefaultPayPalOptions';

import PurchaseSubscriptionSuccess from './PurchaseSubscriptionSuccess';
import PurchaseSubscriptionComponent from './PurchaseSubscriptionComponent';
import { IPaymentPlan } from 'types/payment';

interface Props {
  dispatch: Function,
}

export interface State {
  clientRequestToken?: any,
  error?: any,
  hasSubscribedSuccessfully: boolean,
  isErrorModalOpen: boolean,
  isFetching: boolean,
  isSubmitting: boolean,
  isWaitingForInstance: boolean,
  message?: string,
  overrideNonce?: string,
  plans: IPaymentPlan[],
  recaptchaResponse?: any,
  selectedPlanId?: string,
}

class PurchaseSubscriptionContainer extends React.Component<Props, State> {
  static displayName = 'PurchaseSubscriptionContainer';

  mounted = false;

  paymentService = new PaymentService();

  instance = null;

  state: State = {
    clientRequestToken: undefined,
    hasSubscribedSuccessfully: false,
    isErrorModalOpen: false,
    isFetching: false,
    isSubmitting: false,
    isWaitingForInstance: true,
    message: undefined,
    overrideNonce: undefined,
    plans: [],
    recaptchaResponse: undefined,
    selectedPlanId: undefined,
  };

  /**
   * Component did mount
   * @return {undefined}
   */
  componentDidMount = async () => {
    const { dispatch } = this.props;
    try {
      this.mounted = true;
      this.setState({ isFetching: true });
      // Fetch the payment plans from Braintree
      const { data } = await this.paymentService.fetchPlans();
      const { clientRequestToken, plans } = data;
      // If we're still mounted, update the dialog state
      if (this.mounted) {
        this.setState({
          clientRequestToken,
          plans,
          isFetching: false,
          selectedPlanId: 'FLExceptionalFriendship-GBP',
        });
      }
    } catch (e) {
      if (e instanceof VersionMismatch) {
        dispatch(handleVersionMismatch(e));
      }
      throw e;
    }
  };

  componentWillUnmount = () => {
    this.mounted = false;
  };

  onInstance = (instance: any) => {
    this.instance = instance;
    if (this.mounted) {
      this.setState({ isWaitingForInstance: false });
    }
  };

  handleReCaptchaChange = (recaptchaResponse: any) => {
    this.setState({ recaptchaResponse });
  };

  handleRequestCloseErrorModal = () => {
    this.setState({ isErrorModalOpen: false });
  };

  handleSetOverrideNonce: (value: string) => void = (value: string) => {
    this.setState({ overrideNonce: value });
  };

  /**
   * Handle form
   * @return {undefined}
   */
  handleSubmit = async (e: any) => {
    e.preventDefault();
    const { dispatch } = this.props;
    const {
      isSubmitting,
      overrideNonce,
      recaptchaResponse,
      selectedPlanId: planId,
    } = this.state;

    // If we're currently submitting, then return
    if (isSubmitting) {
      return;
    }

    // Update UI state to show that we're working
    this.setState({ isSubmitting: true });
    // Get stuff from Braintree
    if (this.instance !== null && planId !== undefined) {
      let nonce;
      if ((overrideNonce?.length ?? 0) > 0) {
        nonce = overrideNonce;
      } else {
        // @ts-ignore
        nonce = (await this.instance!.requestPaymentMethod()).nonce;
      }
      try {
        // Make the purchase
        const { data } = await this.paymentService.purchasePlan({ nonce, planId, recaptchaResponse });

        // Fetch subscription details; we don't need to await this since it's just
        // updating the global store
        dispatch(fetchSubscription());

        // If we succeeded, update action & card state
        if (data.isSuccess) {
        } else {
          console.error('Failed to create subscription. Response was:');
          console.error(data);
        }

        // Update state, if still mounted
        if (this.mounted) {
          const { isSuccess, message } = data;
          this.setState({
            hasSubscribedSuccessfully: isSuccess,
            isSubmitting: false,
            message,
          });
        }
      } catch (e) {
        if (e instanceof VersionMismatch) {
          dispatch(handleVersionMismatch(e));
          return;
        }
        this.setState({
          isErrorModalOpen: true,
          hasSubscribedSuccessfully: false,
          isSubmitting: false,
          error: e,
        });
      }
    }
  };

  /**
   * Select currency
   * @return {[type]} [description]
   */
  handleSelectPlan = (e: any) => {
    const plan = e.target.value;
    this.setState({ selectedPlanId: plan });
  };

  /**
   * Render
   * @return {undefined}
   */
  render = () => {
    const {
      clientRequestToken,
      error,
      hasSubscribedSuccessfully,
      isErrorModalOpen,
      isFetching,
      isSubmitting,
      isWaitingForInstance,
      message,
      plans,
      selectedPlanId,
    } = this.state;

    if (!clientRequestToken || isFetching || !selectedPlanId) {
      return <Loading spinner />;
    }

    if (hasSubscribedSuccessfully && message) {
      return <PurchaseSubscriptionSuccess message={message!} />;
    }

    return (
      <Fragment>
        <PurchaseSubscriptionComponent
          isSubmitting={isSubmitting}
          isWaitingForInstance={isWaitingForInstance}
          message={message}
          onInstance={this.onInstance}
          onReCaptchaChange={this.handleReCaptchaChange}
          onSelect={this.handleSelectPlan}
          onSetNonce={this.handleSetOverrideNonce}
          onSubmit={this.handleSubmit}
          options={getDefaultPayPalOptions(clientRequestToken)}
          plans={plans}
          selectedPlanId={selectedPlanId!}
        />
        <PurchaseSubscriptionErrorModal
          isOpen={isErrorModalOpen}
          onRequestClose={this.handleRequestCloseErrorModal}
          error={error}
        />
      </Fragment>
    );
  }
}

export default withRouter(connect()(PurchaseSubscriptionContainer));
